/** Job statuses that can be cancelled (queued / running). */
export const JOB_CANCELLABLE_STATUSES = ['queued', 'running', 'pending', 'cancelling'] as const;

export function canCancelJob(status: string | undefined | null): boolean {
  const normalized = (status || '').toLowerCase();
  return (JOB_CANCELLABLE_STATUSES as readonly string[]).includes(normalized);
}

export function isActiveJobStatus(status: string | undefined | null): boolean {
  return canCancelJob(status);
}

/** Job statuses that allow POST /training/jobs/{id}/retry */
export const TRAINING_JOB_RETRY_STATUSES = ['failed', 'cancelled', 'partially_succeeded'] as const;

export function canRetryTrainingJob(status: string | undefined | null): boolean {
  const normalized = (status || '').toLowerCase();
  return (TRAINING_JOB_RETRY_STATUSES as readonly string[]).includes(normalized);
}

/** Experiment statuses that allow POST /vuser/experiments/{id}/retry */
export function canRetryExperiment(status: string | undefined | null): boolean {
  const normalized = (status || '').toLowerCase();
  return normalized === 'failed' || normalized === 'cancelled';
}

/** Config tuning job statuses that allow POST /auto-tune/optimize/jobs/{id}/retry */
export const OPTIMIZATION_JOB_RETRY_STATUSES = ['failed', 'cancelled'] as const;

export function canRetryOptimizationJob(status: string | undefined | null): boolean {
  const normalized = (status || '').toLowerCase();
  return (OPTIMIZATION_JOB_RETRY_STATUSES as readonly string[]).includes(normalized);
}

export function resolveTrainingJobRecordId(job: unknown): string {
  if (!job || typeof job !== 'object') return '';
  const row = job as Record<string, unknown>;
  return String(row.id ?? row.job_id ?? '').trim();
}

function unwrapTrainingJobRetryPayload(response: unknown): Record<string, unknown> | null {
  if (!response || typeof response !== 'object') return null;
  let cur = response as Record<string, unknown>;
  for (let depth = 0; depth < 4; depth += 1) {
    const inner = cur.data;
    if (!inner || typeof inner !== 'object' || Array.isArray(inner)) break;
    const record = inner as Record<string, unknown>;
    if ('id' in record || 'job_id' in record || 'status' in record || 'integration_id' in record) {
      cur = record;
    } else {
      break;
    }
  }
  return cur;
}

function readTrainingJobIdFromRecord(record: Record<string, unknown> | null | undefined): string | undefined {
  if (!record) return undefined;
  const id = String(record.id ?? record.job_id ?? '').trim();
  return id || undefined;
}

export function extractTrainingJobIdFromResponse(response: unknown): string | undefined {
  const direct = readTrainingJobIdFromRecord(unwrapTrainingJobRetryPayload(response));
  if (direct) return direct;

  const envelope = response as { data?: unknown; id?: unknown; job_id?: unknown } | null | undefined;
  if (!envelope || typeof envelope !== 'object') return undefined;

  const topLevel = readTrainingJobIdFromRecord(envelope as Record<string, unknown>);
  if (topLevel) return topLevel;

  if (envelope.data && typeof envelope.data === 'object' && !Array.isArray(envelope.data)) {
    return readTrainingJobIdFromRecord(envelope.data as Record<string, unknown>);
  }

  return undefined;
}

function readTrainingJobRetryErrorMessage(response: unknown): string | undefined {
  if (!response || typeof response !== 'object') return undefined;
  const row = response as Record<string, unknown>;
  const error = row.error;
  if (error && typeof error === 'object' && !Array.isArray(error)) {
    const message = (error as Record<string, unknown>).message;
    if (typeof message === 'string' && message.trim()) return message.trim();
  }
  if (typeof row.message === 'string' && row.message.trim()) return row.message.trim();
  return undefined;
}

/** Validates retry API envelope and returns the new job id. */
export function assertTrainingJobRetrySucceeded(response: unknown): string {
  if (response && typeof response === 'object') {
    const row = response as Record<string, unknown>;
    if (row.success === false) {
      throw new Error(readTrainingJobRetryErrorMessage(response) || 'Failed to retry fine-tuning job.');
    }
  }

  const newJobId = extractTrainingJobIdFromResponse(response);
  if (!newJobId) {
    throw new Error(
      'The server did not return a new fine-tuning job id. Try again from Connector or refresh the page.'
    );
  }
  return newJobId;
}

export function extractOptimizationJobIdFromResponse(response: unknown): string | undefined {
  return extractTrainingJobIdFromResponse(response);
}

export function buildJobHistoryRetryRowKey(jobId: string, index: number): string {
  const id = String(jobId ?? '').trim();
  return `${id || 'row'}:${index}`;
}

export type JobRetryUiScope =
  | { kind: 'history'; jobId: string; index: number }
  | { kind: 'terminal'; jobId: string }
  | { kind: 'result'; jobId: string }
  | { kind: 'details'; jobId: string }
  | { kind: 'profile-table'; jobId: string; index: number }
  | { kind: 'profile-card'; jobId: string; index: number }
  | { kind: 'profile-modal'; jobId: string };

export function buildJobRetryUiRowKey(scope: JobRetryUiScope): string {
  const id = String(scope.jobId ?? '').trim() || 'row';
  switch (scope.kind) {
    case 'history':
      return buildJobHistoryRetryRowKey(id, scope.index);
    case 'terminal':
      return `retry-ui:terminal:${id}`;
    case 'result':
      return `retry-ui:result:${id}`;
    case 'details':
      return `retry-ui:details:${id}`;
    case 'profile-table':
      return `retry-ui:profile-table:${id}:${scope.index}`;
    case 'profile-card':
      return `retry-ui:profile-card:${id}:${scope.index}`;
    case 'profile-modal':
      return `retry-ui:profile-modal:${id}`;
  }
}

export function isJobRetryUiRowActive(
  retryingRowKey: string | null | undefined,
  scope: JobRetryUiScope
): boolean {
  if (!retryingRowKey) return false;
  return retryingRowKey === buildJobRetryUiRowKey(scope);
}

export function isJobHistoryRetryRowActive(
  retryingRowKey: string | null | undefined,
  jobId: string,
  index: number
): boolean {
  return isJobRetryUiRowActive(retryingRowKey, { kind: 'history', jobId, index });
}

export function isTrainingJobRetryInFlight(
  retryingJobId: string | null | undefined,
  job: unknown
): boolean {
  const id = resolveTrainingJobRecordId(job);
  const retrying = String(retryingJobId ?? '').trim();
  if (!id || !retrying) return false;
  return retrying === id;
}
